import React, { useCallback, useEffect, useRef, useState } from 'react';
import { People } from '../../hooks/api/useExperiences';
import { PeerAvatar } from '../peer-avatar';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';

interface FiPeersonaResultsProps {
  items: Record<string, unknown>[];
  // Kept for API parity with the sibling Fi lanes; unused until KINDLY-972
  // restores the "See more" CTA (Discover-search backends inconsistent).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  searchQuery: string;
}

/** Adapt the backend Peersona result row into the ``People`` shape PeerAvatar expects. */
function asPeersonaPeer(raw: Record<string, unknown>): People | null {
  const lr = raw.listener_role_id;
  const user = raw.user as People['user'] | undefined;
  if (typeof lr !== 'number' || !user?.display_name) return null;
  return {
    about_me: typeof raw.about_me === 'string' ? raw.about_me : '',
    can_take_calls: false,
    group_name: null,
    is_available: Boolean(raw.is_available),
    is_listener: Boolean(raw.is_listener),
    is_peer_active: Boolean(raw.is_peer_active),
    listener_role_id: lr,
    profile_photo_url_square: typeof raw.profile_photo_url_square === 'string' ? raw.profile_photo_url_square : '',
    score: typeof raw.score === 'number' ? raw.score : 0,
    state: typeof raw.state === 'string' ? raw.state : '',
    user,
    chatbot_id: typeof raw.chatbot_id === 'number' ? raw.chatbot_id : null,
  };
}

function CarouselNavChevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="text-gray-800">
      {dir === 'left' ? (
        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

/**
 * Peersona lane in the Fi concierge. Mirrors ``FiListenerResults`` so the
 * Fi lanes feel like one family. This is the only surface that opens the
 * Peersona chat directly from a ``PeerAvatar`` click (``opensPeersonaChatOnClick``);
 * every other surface routes to the peer profile.
 *
 * No "See more" CTA — Discover-page search backends don't yet index
 * Peersona scope_line, so the CTA would land on zero results. Returns
 * once KINDLY-972 unifies the search backends.
 */
export const FiPeersonaResults: React.FC<FiPeersonaResultsProps> = ({ items }) => {
  const isMdUp = useMediaQuery('md');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const showNav = isMdUp && items.length >= 3;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft;
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(left > 0);
    setCanScrollRight(left < max - 1);
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    window.requestAnimationFrame(updateScrollState);
  };

  useEffect(() => {
    updateScrollState();
  }, [items.length, updateScrollState]);

  return (
    <div className="bg-button-secondary w-full rounded-[20px] px-5 py-[18px]">
      <div className="mb-3 flex min-h-[36px] items-center justify-between gap-6">
        <p className="font-albert-sans shrink-0 text-xl font-semibold leading-[1.25] text-black">Peersonas</p>
        {showNav ? (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => scrollBy(-200)}
              disabled={!canScrollLeft}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
            >
              <CarouselNavChevron dir="left" />
            </button>
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => scrollBy(200)}
              disabled={!canScrollRight}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
            >
              <CarouselNavChevron dir="right" />
            </button>
          </div>
        ) : null}
      </div>
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex gap-5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((raw, i) => {
          const peer = asPeersonaPeer(raw);
          if (!peer) return null;
          return (
            <div key={peer.chatbot_id ?? peer.listener_role_id ?? i} className="shrink-0">
              <PeerAvatar peer={peer} isPeersona forcePeersonaLabel opensPeersonaChatOnClick />
            </div>
          );
        })}
      </div>
    </div>
  );
};
